import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartFilterDropdown } from './chart-filter-dropdown';
import { ChartMetricItem } from './chart-metric-item';
import { ChartMetrics } from './chart-metrics';
import { ChartTitle } from './chart-title';
import { useChartHeaderState } from './hooks';
import {
  ChartHeaderContainer,
  MobileGridCellClosedBalance,
  MobileGridCellDropdown,
  MobileGridCellEquity,
  MobileGridCellPnL,
  MobileLayoutGrid,
} from './styled';
import type { ChartHeaderProps } from './types';
import { getMetricDisplayConfig } from './utils';

export const ChartHeader: FC<ChartHeaderProps> = ({
  selectedFilter,
  filterOptions,
  onFilterChange,
}) => {
  const { t } = useTranslation('performanceChart');
  const { chartHeaderData, theme } = useChartHeaderState();

  const pnlConfig = getMetricDisplayConfig('pnl', theme);
  const equityConfig = getMetricDisplayConfig('equity', theme);
  const balanceConfig = getMetricDisplayConfig('balance', theme);

  const pnlMetric = {
    key: 'currentPnL',
    labelKey: 'chart.header.currentPnL' as const,
    tooltipKey: 'chart.header.tooltip.currentPnL' as const,
    value: chartHeaderData.currentPnL,
  };

  const equityMetric = {
    key: 'equity',
    labelKey: 'chart.header.equity' as const,
    tooltipKey: 'chart.header.tooltip.equity' as const,
    value: chartHeaderData.equity,
  };

  const closedBalanceMetric = {
    key: 'closedBalance',
    labelKey: 'chart.header.closedBalance' as const,
    tooltipKey: 'chart.header.tooltip.closedBalance' as const,
    value: chartHeaderData.closedBalance,
  };

  return (
    <ChartHeaderContainer>
      {/* Desktop: Title */}
      <ChartTitle />

      {/* Desktop: All metrics */}
      <ChartMetrics />

      {/* Desktop: Dropdown */}
      <ChartFilterDropdown
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
      />

      {/* Mobile: Grid Layout - 2x2 grid */}
      <MobileLayoutGrid>
        {/* Top Left: PNL */}
        <MobileGridCellPnL>
          <ChartMetricItem
            label={t(pnlMetric.labelKey)}
            tooltip={t(pnlMetric.tooltipKey)}
            value={pnlMetric.value}
            textColor={pnlConfig.textColor}
            textSize={pnlConfig.textSize}
            labelColor={pnlConfig.labelColor}
          />
        </MobileGridCellPnL>

        {/* Top Right: Dropdown */}
        <MobileGridCellDropdown>
          <ChartFilterDropdown
            selectedFilter={selectedFilter}
            filterOptions={filterOptions}
            onFilterChange={onFilterChange}
            mobile={true}
          />
        </MobileGridCellDropdown>

        {/* Bottom Left: Equity */}
        <MobileGridCellEquity>
          <ChartMetricItem
            label={t(equityMetric.labelKey)}
            tooltip={t(equityMetric.tooltipKey)}
            value={equityMetric.value}
            textColor={equityConfig.textColor}
            textSize={equityConfig.textSize}
            labelColor={equityConfig.labelColor}
          />
        </MobileGridCellEquity>

        {/* Bottom Right: Closed Balance */}
        <MobileGridCellClosedBalance>
          <ChartMetricItem
            label={t(closedBalanceMetric.labelKey)}
            tooltip={t(closedBalanceMetric.tooltipKey)}
            value={closedBalanceMetric.value}
            textColor={balanceConfig.textColor}
            textSize={balanceConfig.textSize}
            labelColor={balanceConfig.labelColor}
          />
        </MobileGridCellClosedBalance>
      </MobileLayoutGrid>
    </ChartHeaderContainer>
  );
};
